// Centralized responsive system — the SINGLE source of truth for breakpoints.
//
// No screen may hardcode width thresholds or fixed pixel column widths. Read the
// current tier from `useBreakpoints()`, pick per-tier values with
// `useResponsiveValue(...)`, and build fluid sizes with `clamp(...)`.
//
// Tiers (primary vocabulary — mobile / tablet / desktop):
//   < 820        → mobile    (single-column, stacked cards, overlay drawer nav)
//   820 – 1079   → tablet    (condensed grids, icon-rail nav)
//   >= 1080      → desktop   (full docked nav, multi-column tables)
//
// The shell's nav layout was originally written against mobile / compact / wide.
// Those names are preserved as aliases (compact === tablet, wide === desktop) so
// existing call sites keep working while new code uses mobile/tablet/desktop.

import { useWindowDimensions } from 'react-native';

/** Ordered tier identifiers, smallest → largest. */
export const TIERS = ['mobile', 'tablet', 'desktop'] as const;
export type Tier = (typeof TIERS)[number];

/**
 * Lower-bound width (px) where each tier begins. `mobile` starts at 0; the
 * `tablet`/`desktop` values are the thresholds the shell layout already
 * transitions at, so nav behavior is unchanged.
 */
export const Breakpoints = {
  mobile: 0,
  tablet: 820,
  desktop: 1080,
} as const;

/** Max content width (px) for the centered scroll container, per layout intent. */
export const ContentMaxWidth = {
  default: 1200,
  narrow: 1000,
} as const;

/** A value provided per tier. `mobile` is required; larger tiers fall back down. */
export interface ResponsiveValue<T> {
  mobile: T;
  tablet?: T;
  desktop?: T;
}

export interface BreakpointState {
  width: number;
  height: number;
  /** The active tier for the current viewport width. */
  tier: Tier;
  // Primary flags.
  isMobile: boolean;   // width < 820
  isTablet: boolean;   // 820 – 1079
  isDesktop: boolean;  // >= 1080
  // Backward-compatible aliases (do not remove — used by the nav shell).
  isCompact: boolean;  // === isTablet
  isWide: boolean;     // === isDesktop
}

/** Resolve a raw width (px) to its tier. Pure — safe to call anywhere. */
export function tierForWidth(width: number): Tier {
  if (width >= Breakpoints.desktop) return 'desktop';
  if (width >= Breakpoints.tablet) return 'tablet';
  return 'mobile';
}

/**
 * Pick the value for a tier from a ResponsiveValue, cascading DOWN from the
 * requested tier to the nearest defined smaller tier (desktop → tablet → mobile).
 * Pure helper; the hook `useResponsiveValue` wraps it with the live tier.
 */
export function resolveResponsiveValue<T>(value: ResponsiveValue<T>, tier: Tier): T {
  if (tier === 'desktop') return value.desktop ?? value.tablet ?? value.mobile;
  if (tier === 'tablet') return value.tablet ?? value.mobile;
  return value.mobile;
}

/**
 * Build a fluid, viewport-relative size (web `clamp()`), bounded by min/max.
 * On web react-native-web forwards the string straight to CSS; on native, where
 * `clamp()` is unsupported, we fall back to the max bound.
 *
 *   clamp(16, '4vw', 32) → 'clamp(16px, 4vw, 32px)' on web, 32 on native.
 */
export function clamp(minPx: number, preferred: string, maxPx: number): string | number {
  return `clamp(${minPx}px, ${preferred}, ${maxPx}px)`;
}

/** Live viewport tier + flags. Re-renders on resize/rotate. */
export function useBreakpoints(): BreakpointState {
  const { width, height } = useWindowDimensions();
  const tier = tierForWidth(width);
  const isMobile = tier === 'mobile';
  const isTablet = tier === 'tablet';
  const isDesktop = tier === 'desktop';
  return {
    width,
    height,
    tier,
    isMobile,
    isTablet,
    isDesktop,
    isCompact: isTablet,
    isWide: isDesktop,
  };
}

/**
 * Pick a value for the current tier, cascading down from larger tiers.
 *
 *   const cols = useResponsiveValue({ mobile: 1, tablet: 2, desktop: 4 });
 *   const pad  = useResponsiveValue({ mobile: 18, desktop: 32 }); // tablet → 18
 */
export function useResponsiveValue<T>(value: ResponsiveValue<T>): T {
  const { tier } = useBreakpoints();
  return resolveResponsiveValue(value, tier);
}
